"use client";

import { useEffect } from "react";
import { useSiteSettings } from "@/components/site-settings-provider";
import { useCookieConsent } from "@/components/cookie-consent-provider";

interface ScriptInjectorProps {
  position: "head" | "body";
}

interface ParsedScript {
  src?: string;
  content?: string;
  defer?: boolean;
  async?: boolean;
  id?: string;
  attrs: Record<string, string>;
}

function parseScriptTags(html: string): ParsedScript[] {
  const scripts: ParsedScript[] = [];
  const regex = /<script([^>]*)>([\s\S]*?)<\/script>/gi;
  const attrRegex = /(\w[\w-]*)(?:=["']([^"']*)["'])?/g;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const attrString = match[1];
    const content = match[2].trim();
    const attrs: Record<string, string> = {};
    let attrMatch;

    while ((attrMatch = attrRegex.exec(attrString)) !== null) {
      attrs[attrMatch[1]] = attrMatch[2] ?? "";
    }

    scripts.push({
      src: attrs.src || undefined,
      content: content || undefined,
      defer: /\bdefer\b/i.test(attrString),
      async: /\basync\b/i.test(attrString),
      id: attrs.id || undefined,
      attrs,
    });
  }

  if (scripts.length === 0 && html.trim()) {
    scripts.push({
      content: html.trim(),
      id: "inline-raw-script",
      attrs: {},
    });
  }

  return scripts;
}

export function ScriptInjector({ position }: ScriptInjectorProps) {
  const { settings } = useSiteSettings();
  const { ready, settings: cookieSettings, canRunCategory } = useCookieConsent();
  const code = position === "head" ? settings.scripts.analyticsCode : settings.scripts.customJs;
  const requiredCategory = position === "head" ? "analytics" : "marketing";

  useEffect(() => {
    if (!code) return;
    if (!ready) return;

    if (cookieSettings?.isEnabled) {
      const categoryConfig = cookieSettings.content.categories[requiredCategory];
      if (!categoryConfig.enabled || !canRunCategory(requiredCategory)) {
        return;
      }
    }

    const scripts = parseScriptTags(code);
    if (scripts.length === 0) return;

    const target = position === "head" ? document.head : document.body;
    const createdNodes: HTMLScriptElement[] = [];

    for (const [index, script] of scripts.entries()) {
      const scriptKey = script.id || `${position}-${index}-${script.src || script.content || "script"}`;
      const existing = target.querySelector(`script[data-cms-script-key="${CSS.escape(scriptKey)}"]`);
      if (existing) {
        continue;
      }

      const element = document.createElement("script");
      if (script.src) element.src = script.src;
      if (script.defer) element.defer = true;
      if (script.async) element.async = true;
      for (const [name, value] of Object.entries(script.attrs)) {
        if (name === "src" || name === "defer" || name === "async" || name === "id") continue;
        element.setAttribute(name, value);
      }
      if (script.content) element.textContent = script.content;
      element.dataset.cmsScript = position;
      element.dataset.cmsScriptKey = scriptKey;
      target.appendChild(element);
      createdNodes.push(element);
    }

    return () => {
      for (const node of createdNodes) {
        node.remove();
      }
    };
  }, [canRunCategory, code, cookieSettings, position, ready, requiredCategory]);

  return null;
}
